import React from 'react';
import useScrollReveal from '../hooks/useScrollReveal';

const services = [
  { number: '01', title: 'Plot Selection', description: 'Expert guidance to find the perfect plot based on your goals and budget.' },
  { number: '02', title: 'Search Report', description: 'Legal due diligence — title verification, encumbrance check, ownership history.' },
  { number: '03', title: 'Plotting', description: 'Accurate measurements, boundary markings, and layout planning per norms.' },
  { number: '04', title: 'Paper Notice', description: 'Legal notice publication ensuring transparent ownership transfer.' },
  { number: '05', title: 'Registration', description: 'Complete government registration, stamp duty, and documentation.' },
  { number: '06', title: 'Name Board', description: 'Name board installation for clear identification and ownership declaration.' },
  { number: '07', title: 'Demarcation', description: 'Professional boundary demarcation with survey markers on ground.' },
  { number: '08', title: 'Possession', description: 'Final handover with complete documentation and post-purchase support.' },
];

const Pricing = () => {
  const [headRef, headVisible] = useScrollReveal();
  const [stepsRef, stepsVisible] = useScrollReveal({ threshold: 0.1 });

  return (
    <section id="services" className="py-24 lg:py-32 relative overflow-hidden bg-gray-100 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header — split layout */}
        <div ref={headRef} className={`grid lg:grid-cols-2 gap-10 lg:gap-20 mb-20 transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <span className="inline-block text-[11px] tracking-[0.3em] uppercase font-semibold text-accent-500 mb-5">
              End-to-End Support
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading text-dark-800 leading-tight">
              Our{' '}
              <span className="italic text-primary-600">8-Step</span> Process
            </h2>
          </div>
          <div className="flex items-end">
            <p className="text-dark-400 text-base leading-relaxed font-light">
              From plot selection to possession — we guide you through every step
              of the land investment process with complete transparency.
            </p>
          </div>
        </div>

        {/* Process steps — 2x4 with connecting visual */}
        <div ref={stepsRef} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {services.map((service, i) => (
            <div
              key={i}
              className={`relative group transition-all duration-700 ${
                stepsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: stepsVisible ? `${i * 80}ms` : '0ms' }}
            >
              <div className="bg-white rounded-2xl p-6 lg:p-7 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-500 h-full">
                {/* Step number */}
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-4xl font-heading text-primary-100 group-hover:text-accent-200 transition-colors duration-500 leading-none select-none">
                    {service.number}
                  </span>
                  <div className="h-px flex-1 bg-gray-100 group-hover:bg-accent-200 transition-colors duration-500" />
                </div>

                <h3 className="font-heading text-lg text-dark-800 leading-snug mb-3 group-hover:text-primary-700 transition-colors duration-500">
                  {service.title}
                </h3>

                <p className="text-[13px] text-dark-400 font-light leading-relaxed">
                  {service.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-14">
          <button onClick={() => window.dispatchEvent(new Event('openContactModal'))} className="inline-flex items-center gap-2.5 bg-primary-600 text-white rounded-full px-7 py-3 text-[11px] font-semibold tracking-wider uppercase hover:bg-primary-700 hover:shadow-lg transition-all duration-500 group">
            Get Started Today
            <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
          <span className="text-[11px] text-dark-300 font-light tracking-wide">All services with complete legal compliance</span>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
